/**
 * Competition Profiles Service
 *
 * API layer for managing competitor reference profiles.
 */

import { apiClient } from "@/services/apiClient";
import {
  CompetitionProfile,
  CompetitionPlatform,
  CompetitionProfilesResponse,
  CreateCompetitionProfileRequest,
  UpdateCompetitionProfileRequest,
} from "../types";

class CompetitionService {
  private handleError(error: unknown, defaultMessage: string): never {
    console.error(`CompetitionService Error: ${defaultMessage}`, error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(defaultMessage);
  }

  async getProfiles(
    clientId: string,
    platform?: CompetitionPlatform
  ): Promise<CompetitionProfile[]> {
    try {
      const params = new URLSearchParams();
      if (platform) {
        params.append("platform", platform);
      }
      const queryString = params.toString();
      const url = `/api/clients/${clientId}/competition-profiles${queryString ? `?${queryString}` : ""}`;
      const response = await apiClient.get<CompetitionProfilesResponse>(url);
      return response.profiles;
    } catch (error) {
      this.handleError(error, "Failed to fetch competition profiles");
    }
  }

  async getProfile(profileId: string): Promise<CompetitionProfile> {
    try {
      const response = await apiClient.get<CompetitionProfile>(
        `/api/competition-profiles/${profileId}`
      );
      return response;
    } catch (error) {
      this.handleError(error, "Failed to fetch competition profile");
    }
  }

  async createProfile(
    clientId: string,
    data: CreateCompetitionProfileRequest
  ): Promise<CompetitionProfile> {
    try {
      const response = await apiClient.post<CompetitionProfile>(
        `/api/clients/${clientId}/competition-profiles`,
        data
      );
      return response;
    } catch (error) {
      this.handleError(error, "Failed to create competition profile");
    }
  }

  async updateProfile(
    profileId: string,
    data: UpdateCompetitionProfileRequest
  ): Promise<CompetitionProfile> {
    try {
      const response = await apiClient.patch<CompetitionProfile>(
        `/api/competition-profiles/${profileId}`,
        data
      );
      return response;
    } catch (error) {
      this.handleError(error, "Failed to update competition profile");
    }
  }

  async deleteProfile(profileId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/competition-profiles/${profileId}`);
    } catch (error) {
      this.handleError(error, "Failed to delete competition profile");
    }
  }
}

export const competitionService = new CompetitionService();
export default competitionService;
